import { useHasApiKey } from "@crm/ai/user-ai-credentials";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import { Button } from "@crm/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crm/ui/components/ui/dialog";
import { Input } from "@crm/ui/components/ui/input";
import { Label } from "@crm/ui/components/ui/label";
import { Textarea } from "@crm/ui/components/ui/textarea";
import { createFileRoute } from "@tanstack/react-router";
import { Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import {
  CheckCircle2,
  Copy,
  Download,
  Link2,
  Loader2,
  Megaphone,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { WorkbenchDrawer } from "#components/workbench-drawer";
import {
  type AdGroupListItem,
  useAdGroup,
  useAdGroups,
  useCreateAdGroup,
  useDeleteAdGroup,
  useUpdateAdGroup,
} from "#app/sio/ad-groups.queries";
import { ADS_RULES, getCharacterLength } from "#app/sio/ads-rules";
import { getModelsByScenario, isModelInList, normalizeModelId } from "#app/sio/models";
import { useBusinessInfo, useSioSettings } from "#app/sio/use-sio-settings";
import { useActiveSite, useToolGap } from "#app/sites";
import { ToolGapCard } from "#components/tool-gap-card";
import { ModelSelect } from "../../-components/model-select";
import { useEnsureModelList } from "../../-hooks/use-ensure-model-list";
import { useSettingsStore } from "../../-store/settings-store";
import { exportToExcel, parseAdsContent, runAdsGenerationEffect } from "./-lib/ads-content";

export const Route = createFileRoute("/_protected/sio-tools/_layout/ads/")({
  head: () => ({}),
  component: AdGroupsPage,
});

// ============================================================
// 广告组列表（付费线主页）
// ============================================================

function AdGroupsPage() {
  const groupsResult = useAdGroups();
  const groups = AsyncResult.value(groupsResult).pipe(Option.getOrElse(() => []));
  const isLoading = AsyncResult.isWaiting(groupsResult);
  const hasApiKey = useHasApiKey();
  const toolGap = useToolGap(hasApiKey);
  const activeSite = useActiveSite();
  const deleteGroup = useDeleteAdGroup();
  const [showCreate, setShowCreate] = useState(false);
  const [workbenchGroupId, setWorkbenchGroupId] = useState<string | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<AdGroupListItem | null>(null);

  if (toolGap.status === "loading") {
    return <div className="mx-auto max-w-5xl p-8 text-[13px] text-ink-3">加载中…</div>;
  }

  if (toolGap.gap) {
    return (
      <div className="mx-auto max-w-5xl p-8">
        <h1 className="mb-6 font-display font-semibold text-[28px] text-ink tracking-tight">
          广告<span className="font-display text-brand italic">投放</span>
        </h1>
        <ToolGapCard
          gap={toolGap.gap}
          settingsTo="/sio-tools/settings"
          siteId={activeSite?.id}
          siteName={activeSite?.name}
          variant="page"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-8">
      {/* Hero */}
      <div className="mb-6 flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="mb-1.5 font-display font-semibold text-[28px] text-ink tracking-tight">
            广告<span className="font-display text-brand italic">投放</span>
          </h1>
          <p className="max-w-xl text-[13.5px] text-ink-2 leading-relaxed">
            关键词 → 广告组 → RSA 文案 → 投放。广告推广的是着陆页，不是文章。
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} type="button">
          <Plus className="size-4" />
          新建广告组
        </Button>
      </div>

      {isLoading ? (
        <div className="py-10 text-[13px] text-ink-3">加载中…</div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border border-dashed px-6 py-14 text-center">
          <Megaphone className="mb-4 h-10 w-10 text-ink-3" />
          <p className="mb-4 text-[13px] text-ink-3">
            还没有广告组。从一簇关键词 + 一个着陆页开始。
          </p>
          <Button onClick={() => setShowCreate(true)} size="sm" variant="outline">
            <Plus className="size-4" />
            新建第一个广告组
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {groups.map((group) => (
            <AdGroupCard
              group={group}
              key={group.id}
              onDelete={() => setDeletingGroup(group)}
              onOpen={() => setWorkbenchGroupId(group.id)}
            />
          ))}
        </div>
      )}

      {/* 新建对话框 */}
      <CreateAdGroupDialog
        onClose={() => setShowCreate(false)}
        onCreated={(id) => setWorkbenchGroupId(id)}
        open={showCreate}
      />

      {/* 删除确认 */}
      <Dialog onOpenChange={(open) => !open && setDeletingGroup(null)} open={!!deletingGroup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            确定删除广告组「{deletingGroup?.name}
            」吗？文案与关键词配置将一并删除。
          </p>
          <DialogFooter>
            <Button onClick={() => setDeletingGroup(null)} variant="outline">
              取消
            </Button>
            <Button
              disabled={deleteGroup.isPending}
              onClick={() => {
                if (!deletingGroup) {
                  return;
                }
                void deleteGroup
                  .executePromise(deletingGroup.id)
                  .then(() => setDeletingGroup(null));
              }}
              variant="destructive"
            >
              {deleteGroup.isPending ? "删除中..." : "删除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 广告组工位抽屉 */}
      {workbenchGroupId && (
        <AdGroupWorkbenchDrawer
          groupId={workbenchGroupId}
          onClose={() => setWorkbenchGroupId(null)}
        />
      )}
    </div>
  );
}

function AdGroupCard({
  group,
  onOpen,
  onDelete,
}: {
  group: AdGroupListItem;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const keywords = group.keywords ?? [];
  const visibleKeywords = keywords.slice(0, 2);
  const hiddenCount = keywords.length - visibleKeywords.length;

  return (
    <button
      className="rounded-2xl border border-border bg-card p-4.5 text-left shadow-s transition-all hover:-translate-y-px hover:border-border-strong hover:shadow-m"
      onClick={onOpen}
      type="button"
    >
      <div className="mb-2 flex items-center gap-2.5">
        <span className="font-bold text-[15px] text-ink tracking-tight">{group.name}</span>
        {group.hasContent ? (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-0.5 font-semibold text-[11px] text-success">
            <CheckCircle2 size={11} />
            文案就绪
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center rounded-full bg-warning-soft px-2.5 py-0.5 font-semibold text-[11px] text-warning">
            待生成
          </span>
        )}
      </div>
      <div className="mb-2.5 flex flex-wrap gap-1.5">
        {visibleKeywords.map((kw) => (
          <span
            className="rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-[10.5px] text-ink-2"
            key={kw}
          >
            {kw}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[10.5px] text-ink-3">
            +{hiddenCount}
          </span>
        )}
        {keywords.length === 0 && <span className="text-[11px] text-ink-3">未添加关键词</span>}
      </div>
      <div className="mb-3.5 flex items-center gap-1.5 text-[11.5px] text-ink-3">
        <Link2 size={12} />
        着陆页
        {group.landingPageUrl ? (
          <span className="font-mono text-[11px] text-info">{group.landingPageUrl}</span>
        ) : (
          <span>未设置</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-brand-soft px-3 py-1.5 font-semibold text-[12px] text-brand-text">
          {group.hasContent ? "编辑文案" : "生成文案"}
        </span>
        <span
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-danger-soft hover:text-danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              onDelete();
            }
          }}
          role="button"
          tabIndex={0}
          title="删除广告组"
        >
          <Trash2 size={14} />
        </span>
      </div>
    </button>
  );
}

function CreateAdGroupDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const createGroup = useCreateAdGroup();
  const [name, setName] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [landingPageUrl, setLandingPageUrl] = useState("");

  const handleCreate = () => {
    const keywords = keywordsText
      .split("\n")
      .map((k) => k.trim())
      .filter(Boolean);
    void createGroup
      .executePromise({
        name: name.trim(),
        keywords,
        ...(landingPageUrl.trim() ? { landingPageUrl: landingPageUrl.trim() } : {}),
      })
      .then((group) => {
        setName("");
        setKeywordsText("");
        setLandingPageUrl("");
        onClose();
        onCreated(group.id);
      });
  };

  return (
    <Dialog onOpenChange={(isOpen) => !isOpen && onClose()} open={open}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>新建广告组</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>广告组名称</Label>
            <Input
              onChange={(e) => setName(e.target.value)}
              placeholder="如：硅胶厨具 · 批发词"
              value={name}
            />
          </div>
          <div className="space-y-2">
            <Label>关键词（每行一个）</Label>
            <Textarea
              className="min-h-[120px]"
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder={"silicone kitchenware wholesale\nbulk silicone utensils"}
              value={keywordsText}
            />
          </div>
          <div className="space-y-2">
            <Label>着陆页 URL（可稍后设置）</Label>
            <Input
              onChange={(e) => setLandingPageUrl(e.target.value)}
              placeholder="https://your-site.com/landing-page"
              value={landingPageUrl}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            取消
          </Button>
          <Button disabled={!name.trim() || createGroup.isPending} onClick={handleCreate}>
            {createGroup.isPending ? "创建中..." : "创建并打开工位"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// 广告组工位（RSA 文案抽屉）
// ============================================================

function AdGroupWorkbenchDrawer({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const groupResult = useAdGroup(groupId);
  const group = AsyncResult.value(groupResult).pipe(Option.getOrUndefined);
  const isLoading = AsyncResult.isWaiting(groupResult);
  const updateGroup = useUpdateAdGroup();
  const hasApiKey = useHasApiKey();
  const toolGap = useToolGap(hasApiKey);
  const activeSite = useActiveSite();
  const businessInfo = useBusinessInfo();
  const sioSettings = AsyncResult.value(useSioSettings()).pipe(Option.getOrUndefined);
  const adsDefaultModel = sioSettings?.adsDefaultModel ?? "";
  const { cachedModels, addBlockedModel } = useSettingsStore();
  useEnsureModelList();

  const [landingPage, setLandingPage] = useState<string | null>(null);
  const [selectedModelOverride, setSelectedModelOverride] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const selectedModel = selectedModelOverride ?? adsDefaultModel;
  const availableModels = getModelsByScenario("ads");

  const parsed = useMemo(
    () => (group?.content ? parseAdsContent(group.content) : null),
    [group?.content],
  );

  const isConfigured = toolGap.status === "ready" && toolGap.gap === null;

  const handleSaveLandingPage = () => {
    if (landingPage === null || !group) {
      return;
    }
    void updateGroup
      .executePromise({ id: group.id, landingPageUrl: landingPage || null })
      .then(() => setLandingPage(null));
  };

  const handleGenerate = () => {
    if (!group || !businessInfo) {
      return;
    }
    const keywords = (group.keywords ?? []).filter(Boolean);
    const model = normalizeModelId(selectedModel);
    if (!isModelInList(model, cachedModels)) {
      toast.error("当前模型不可用，请重新选择");
      return;
    }

    setIsGenerating(true);
    void browserEffectRuntime
      .runPromise(
        runAdsGenerationEffect({
          keywords,
          businessInfo,
          model,
          cachedModels,
          saveContent: (content) => updateGroup.executePromise({ id: group.id, content }),
          onModelBlocked: addBlockedModel,
        }),
      )
      .then((outcome) => {
        if (outcome.bestEffort) {
          toast.warning("校验未完全通过，已保存最佳结果，请手动微调");
        } else {
          toast.success("文案生成成功");
        }
      })
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : "生成失败");
      })
      .finally(() => setIsGenerating(false));
  };

  const handleCopyAll = () => {
    if (!group?.content) {
      return;
    }
    void navigator.clipboard
      .writeText(group.content)
      .then(() => toast.success("已复制全部文案"))
      .catch(() => toast.error("复制失败"));
  };

  const handleExport = () => {
    if (!group?.content) {
      return;
    }
    exportToExcel(group.content, (group.keywords ?? []).join("\n"));
  };

  return (
    <WorkbenchDrawer
      onClose={onClose}
      subtitle={
        <>
          {group?.content ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-0.5 font-semibold text-[11px] text-success">
              <CheckCircle2 size={11} />
              文案就绪
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-warning-soft px-2.5 py-0.5 font-semibold text-[11px] text-warning">
              待生成
            </span>
          )}
          <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 font-semibold text-[11px] text-ink-2">
            {(group?.keywords ?? []).length} 关键词
          </span>
        </>
      }
      title={group?.name ?? "广告组"}
      wide
    >
      {isLoading || !group ? (
        <div className="py-8 text-[13px] text-ink-3">加载中…</div>
      ) : !isConfigured ? (
        toolGap.status === "loading" ? (
          <div className="py-8 text-[13px] text-ink-3">加载中…</div>
        ) : toolGap.gap ? (
          <ToolGapCard
            gap={toolGap.gap}
            settingsTo="/sio-tools/settings"
            siteId={activeSite?.id}
            siteName={activeSite?.name}
            variant="page"
          />
        ) : null
      ) : (
        <div className="space-y-4">
          {/* 关键词簇 + 着陆页 */}
          <div className="rounded-2xl border border-border bg-card p-4.5">
            <div className="mb-2 font-semibold text-[12.5px] text-ink-3 uppercase tracking-wider">
              关键词簇
            </div>
            <div className="mb-4 flex flex-wrap gap-1.5">
              {(group.keywords ?? []).map((kw) => (
                <span
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-[11px] text-ink-2"
                  key={kw}
                >
                  {kw}
                </span>
              ))}
              {(group.keywords ?? []).length === 0 && (
                <span className="text-[12px] text-ink-3">未添加关键词（在列表页重建广告组）</span>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[12.5px]">着陆页 URL</Label>
              <div className="flex gap-2">
                <Input
                  onChange={(e) => setLandingPage(e.target.value)}
                  placeholder="https://your-site.com/landing-page"
                  value={landingPage ?? group.landingPageUrl ?? ""}
                />
                {landingPage !== null && landingPage !== (group.landingPageUrl ?? "") && (
                  <Button
                    disabled={updateGroup.isPending}
                    onClick={handleSaveLandingPage}
                    size="sm"
                  >
                    保存
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* 模型 + 生成 */}
          <div className="rounded-2xl border border-border bg-card p-4.5">
            <div className="mb-3 space-y-2">
              <Label className="text-[12.5px]">AI 模型</Label>
              <ModelSelect
                allowCustom={false}
                apiModels={cachedModels}
                commonModels={availableModels}
                customValue=""
                isCustom={false}
                onChange={setSelectedModelOverride}
                value={selectedModel}
              />
            </div>
            <Button
              className="w-full"
              disabled={isGenerating || (group.keywords ?? []).length === 0}
              onClick={handleGenerate}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  生成中（可能多次校验修补）…
                </>
              ) : (
                <>
                  <RefreshCw className="size-4" />
                  {group.content ? "重新生成文案" : "生成文案"}
                </>
              )}
            </Button>
          </div>

          {/* 文案展示 */}
          {parsed && (
            <>
              <RsaSection
                title={`标题 Headlines · ${parsed.headlines.length} 条 · 上限 ${ADS_RULES.headlineMaxChars} 字符`}
              >
                {parsed.headlines.map((line, idx) => (
                  <RsaLine key={`h-${idx}`} limit={ADS_RULES.headlineMaxChars} text={line} />
                ))}
              </RsaSection>
              <RsaSection
                title={`描述 Descriptions · ${parsed.descriptions.length} 条 · 上限 ${ADS_RULES.descriptionMaxChars} 字符`}
              >
                {parsed.descriptions.map((line, idx) => (
                  <RsaLine key={`d-${idx}`} limit={ADS_RULES.descriptionMaxChars} text={line} />
                ))}
              </RsaSection>
              {(parsed.callouts.length > 0 || parsed.siteLinks.length > 0) && (
                <RsaSection title="附加信息 Assets">
                  {parsed.callouts.map((line, idx) => (
                    <RsaLine badge="Callout" key={`c-${idx}`} text={line} />
                  ))}
                  {parsed.siteLinks.map((sl, idx) => (
                    <RsaLine
                      badge="Sitelink"
                      key={`s-${idx}`}
                      text={[sl.title, sl.url, sl.desc1, sl.desc2].filter(Boolean).join(" — ")}
                    />
                  ))}
                </RsaSection>
              )}

              {/* 动作行 */}
              <div className="flex items-center gap-2.5">
                <Button onClick={handleCopyAll} size="sm" variant="outline">
                  <Copy className="size-3.5" />
                  复制全部
                </Button>
                <Button onClick={handleExport} size="sm">
                  <Download className="size-3.5" />
                  导出 Google Ads 格式
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </WorkbenchDrawer>
  );
}

function RsaSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-2 border-border border-b bg-muted/60 px-4 py-2.5">
        <h4 className="font-bold text-[13px] text-ink">{title}</h4>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

function RsaLine({ text, limit, badge }: { text: string; limit?: number; badge?: string }) {
  const length = getCharacterLength(text);
  const over = limit !== undefined && length > limit;
  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5">
      {badge && (
        <span className="shrink-0 rounded-full bg-info-soft px-2 py-0.5 font-semibold text-[10px] text-info">
          {badge}
        </span>
      )}
      <span className="min-w-0 flex-1 text-[12.5px] text-ink leading-relaxed">{text}</span>
      {limit !== undefined && (
        <span
          className={`shrink-0 font-mono font-semibold text-[10.5px] ${over ? "text-danger" : "text-success"}`}
        >
          {length}/{limit}
        </span>
      )}
      <button
        className="flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-muted hover:text-ink"
        onClick={() => {
          void navigator.clipboard
            .writeText(text)
            .then(() => toast.success("已复制"))
            .catch(() => toast.error("复制失败"));
        }}
        title="复制"
        type="button"
      >
        <Copy size={13} />
      </button>
    </div>
  );
}
